import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useGetPieceByIdQuery, useDeletePieceMutation } from '../../redux/api/piecesApi';
import PieceImageUpload from '../../components/PieceImageUpload';
import { toast } from 'react-toastify';

const PieceDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const { data: piece, isLoading, error } = useGetPieceByIdQuery(id);
    const [deletePiece] = useDeletePieceMutation();

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this piece?')) {
            try {
                await deletePiece({ pieceId: piece.piece_id, productId: piece.product_id }).unwrap();
                toast.success('Piece deleted successfully');
                navigate(`/products/${piece.product_id}`);
            } catch (err) {
                toast.error(err?.data?.message || err?.error || 'Failed to delete piece');
            }
        }
    };

    if (isLoading) {
        return (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
        );
    }

    if (error) {
        return (
                <div className="alert alert-danger">
                    Error: {error?.data?.message || error?.error || 'Failed to load piece'}
                </div>
        );
    }

    if (!piece) {
        return (
                <div className="alert alert-warning">
                    Piece not found
                </div>
        );
    }

    return (
            <div className="container-fluid">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h1 className="h2 mb-1">
                            {piece.internal_manufacturer_code}
                        </h1>
                        <p className="text-muted">Piece Details</p>
                    </div>
                    <div className="btn-group" role="group">
                        <Link
                            to={`/products/${piece.product_id}`}
                            className="btn btn-secondary"
                        >
                            <i className="bi bi-arrow-left me-1"></i>
                            Back to Product
                        </Link>
                        <Link
                            to={`/pieces/${piece.piece_id}/edit`}
                            className="btn btn-primary"
                        >
                            <i className="bi bi-pencil me-1"></i>
                            Edit Piece
                        </Link>
                        <button
                            onClick={handleDelete}
                            className="btn btn-danger"
                        >
                            <i className="bi bi-trash me-1"></i>
                            Delete Piece
                        </button>
                    </div>
                </div>

                {/* Piece Information */}
                <div className="card mb-4">
                    <div className="card-header">
                        <h5 className="card-title mb-0">Piece Information</h5>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">
                                    Internal Manufacturer Code
                                </label>
                                <p className="form-control-plaintext">{piece.internal_manufacturer_code}</p>
                            </div>
                            
                            {piece.ean_code && (
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold">
                                        EAN Code
                                    </label>
                                    <p className="form-control-plaintext">{piece.ean_code}</p>
                                </div>
                            )}
                            
                            {piece.qr_code && (
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-bold">
                                        QR Code
                                    </label>
                                    <p className="form-control-plaintext">{piece.qr_code}</p>
                                </div>
                            )}
                            
                            {piece.description && (
                                <div className="col-12 mb-3">
                                    <label className="form-label fw-bold">
                                        Description
                                    </label>
                                    <p className="form-control-plaintext">{piece.description}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Piece Images */}
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title mb-0">Piece Images</h5>
                    </div>
                    <div className="card-body">
                        <PieceImageUpload pieceId={piece.piece_id} />
                    </div>
                </div>
            </div>
    );
};

export default PieceDetailsPage;
